<script setup lang="ts">
import type { NavigationPageScale } from "../../../types/blocks/navigation/NavigationPageOption";
import { FIELD_BY_KEY, formatValue, getField } from "./fieldRegistry";

const props = defineProps<{
  resolved: {
    title?: string;
    description?: string;
    icon?: string;
    iconColor?: string;
    image?: string;
    url?: string;
    to?: string;
    external?: boolean;
    interactive?: boolean;
    classes?: {
      card?: string;
      description?: string;
      icon?: string;
      interactive?: string;
    };
  };
  scale: NavigationPageScale;
}>();

const scaleClasses: Record<NavigationPageScale, { title: string; description: string; icon: string }> = {
  sm: {
    title: "text-lg",
    description: "text-sm",
    icon: "size-10",
  },
  md: {
    title: "text-xl",
    description: "text-base",
    icon: "size-12",
  },
  lg: {
    title: "text-2xl",
    description: "text-lg",
    icon: "size-14",
  },
  xl: {
    title: "text-3xl",
    description: "text-xl",
    icon: "size-16",
  },
};

const destination = computed(() =>
  String(props.resolved.url ?? props.resolved.to ?? ""),
);
</script>

<template>
  <div class="space-y-3">
    <div class="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      <Icon mode="svg" size="12" name="lucide:eye" />
      Resolved card
    </div>

    <Card
      :class="[
        'pointer-events-none flex min-h-[180px] flex-col rounded-[14px] p-0 shadow-sm',
        resolved.classes?.card,
        resolved.classes?.interactive,
      ]"
    >
      <CardHeader class="px-[22px] pb-2 pt-[22px]">
        <CardTitle :class="scaleClasses[scale].title">
          {{ resolved.title || "Untitled link" }}
        </CardTitle>
        <CardDescription :class="[scaleClasses[scale].description, resolved.classes?.description]">
          {{ resolved.description || "Add a short description for this destination." }}
        </CardDescription>
      </CardHeader>
      <CardContent class="mt-auto flex justify-end px-[22px] pb-[22px] pt-0">
        <img
          v-if="resolved.image"
          :src="resolved.image"
          :alt="resolved.title || 'Navigation item image'"
          class="max-h-[92px] rounded-md object-cover"
        />
        <Icon
          v-else
          mode="svg"
          :name="resolved.icon || 'lucide:circle-arrow-right'"
          :class="[scaleClasses[scale].icon, resolved.classes?.icon]"
          :style="{ color: resolved.iconColor || undefined }"
        />
      </CardContent>
    </Card>

    <div class="space-y-2">
      <p class="text-[11px] font-semibold text-foreground">
        {{ destination || "No destination yet" }}
      </p>
      <div class="flex flex-wrap gap-1.5">
        <code class="rounded bg-secondary px-1.5 py-0.5 text-[10.5px]">
          {{ destination || "No destination" }}
        </code>
        <span class="rounded-full border px-1.5 py-0.5 text-[10px] font-medium">
          {{ formatValue(FIELD_BY_KEY.external, getField(resolved, FIELD_BY_KEY.external)) }}
        </span>
        <span class="rounded-full border px-1.5 py-0.5 text-[10px] font-medium">
          {{ formatValue(FIELD_BY_KEY.interactive, getField(resolved, FIELD_BY_KEY.interactive)) }}
        </span>
      </div>
    </div>
  </div>
</template>
